import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import LessonUpdatedChip from './LessonUpdatedChip';
import { isLessonCompleted, isLessonUpdated, lessonWatchedRatio } from '../useTutorialProgress';

/**
 * Uma lição na lista: número (ou check se assistida), título, aviso de atualização e barra do quanto já viu.
 * @param {Object} lesson - { id, number, title }
 * @param {Number} index - posição no capítulo (0-based), usada quando a lição não tem número
 * @param {Boolean} selected
 * @param {Object} progress - progresso salvo da lição (opcional)
 * @param {Function} onClick
 * @param {Boolean} compact - versão menor, para o menu lateral da página Tutoriais
 */
const LessonRow = ({ lesson, index, selected, progress = null, onClick, compact = false }) => {
  const badgeSize = compact ? 20 : 28;
  const completed = isLessonCompleted(progress);
  const watchedRatio = completed ? 0 : lessonWatchedRatio(progress);
  let badgeBackground = ColorPalette.bgMedium;
  if (selected) badgeBackground = `linear-gradient(135deg, ${ColorPalette.blueMain} 0%, #4A6BFF 100%)`;
  else if (completed) badgeBackground = ColorPalette.successLight;
  let badgeColor = ColorPalette.textMedium;
  if (selected) badgeColor = ColorPalette.white;
  else if (completed) badgeColor = '#2E7D32';

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        gap: compact ? 1 : 1.5,
        px: compact ? 1 : 1.25,
        py: compact ? 0.6 : 1,
        borderRadius: compact ? '8px' : '12px',
        backgroundColor: selected ? ColorPalette.blueAlpha : 'transparent',
        transition: 'background-color 0.2s ease',
        '&:hover': { backgroundColor: selected ? ColorPalette.blueAlpha : ColorPalette.bgMain }
      }}
    >
      <Box
        sx={{
          width: badgeSize,
          height: badgeSize,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: compact ? '0.68rem' : '0.8rem',
          fontWeight: 700,
          background: badgeBackground,
          color: badgeColor
        }}
      >
        {completed ? <CheckIcon sx={{ fontSize: compact ? 13 : 16 }} /> : (lesson.number ?? index + 1)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: compact ? '0.8rem' : '0.92rem',
            fontWeight: selected ? 600 : 500,
            color: selected ? ColorPalette.blueMain : ColorPalette.textDark,
            lineHeight: 1.35
          }}
        >
          {lesson.title}
        </Typography>
        {isLessonUpdated(lesson, progress) && (
          <Box sx={{ mt: 0.5 }}><LessonUpdatedChip size="tiny" /></Box>
        )}
        {watchedRatio > 0 && (
          <Box sx={{ mt: compact ? 0.5 : 0.75, height: 3, borderRadius: 2, backgroundColor: ColorPalette.bgMedium, overflow: 'hidden' }}>
            <Box sx={{ width: `${Math.round(watchedRatio * 100)}%`, height: '100%', backgroundColor: ColorPalette.blueMain }} />
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
};

export default LessonRow;
